// Pure view-model logic for the provider-neutral calendar settings page.
// Kept free of React/urql so it is unit-testable under vitest (node env).

import {
  CalendarConnectionSummary,
  CalendarIntegrationStatus,
  CalendarMigrationStatus,
  CalendarProvider,
  ExternalCalendarSummary
} from "./types"

export const PROVIDER_LABELS: Record<CalendarProvider, string> = {
  google: "Google Calendar",
  microsoft: "Microsoft Outlook",
  apple: "Apple iCloud Calendar",
  ical_feed: "iCal feed"
}

export const providerLabel = (provider: CalendarProvider): string => PROVIDER_LABELS[provider] || "Calendar"

export const implementationLabel = (implementation: CalendarConnectionSummary["implementation"]): string =>
  implementation === "native" ? "Heal.me sync" : "Legacy sync"

export type HealthTone = "ok" | "pending" | "warning" | "error"

export interface ConnectionHealth {
  tone: HealthTone
  label: string
  needsReauth: boolean
}

export const connectionHealth = (connection: CalendarConnectionSummary): ConnectionHealth => {
  switch (connection.status) {
    case "active":
      return { tone: "ok", label: "Connected", needsReauth: false }
    case "pending":
    case "syncing":
      return { tone: "pending", label: "Syncing", needsReauth: false }
    case "degraded":
      return { tone: "warning", label: "Sync issues", needsReauth: false }
    case "reauth_required":
      return { tone: "error", label: "Reconnect needed", needsReauth: true }
    case "disconnecting":
      return { tone: "warning", label: "Disconnecting", needsReauth: false }
    case "failed":
      return { tone: "error", label: "Connection failed", needsReauth: true }
    default:
      return { tone: "warning", label: "Disconnected", needsReauth: false }
  }
}

// Cards for disconnected connections are hidden; everything else stays
// visible even if the provider is no longer configured (read-only card).
export const visibleConnections = (connections: CalendarConnectionSummary[]): CalendarConnectionSummary[] =>
  connections.filter((connection) => connection.status !== "disconnected")

export const OAUTH_PROVIDERS: CalendarProvider[] = ["google", "microsoft"]

// Connect CTAs are strictly backend-gated: a provider renders a CTA only when
// it appears in `connectableProviders`. Never infer configuration client-side.
export const isProviderConnectable = (provider: CalendarProvider, connectableProviders: CalendarProvider[]): boolean =>
  connectableProviders.includes(provider)

export interface AuthorizeUrlOptions {
  // Signs the migration linkage for a legacy Cronofy connection (the backend
  // also derives a login_hint from it so the right account is pre-selected).
  legacyConnectionPublicId?: string
  // Forces the provider's account chooser — used when adding another account
  // of an already-connected provider, since providers otherwise silently
  // reuse the active browser session.
  selectAccount?: boolean
  // Pre-selects the account of an existing connection (reauth flows).
  loginHintConnectionPublicId?: string
  // Forces the provider consent screen so previously granted (possibly
  // incomplete) scopes are not silently reused.
  reconsent?: boolean
}

export const authorizeUrl = (provider: CalendarProvider, options: AuthorizeUrlOptions = {}): string | null => {
  if (!OAUTH_PROVIDERS.includes(provider)) return null

  const params = new URLSearchParams()
  if (options.legacyConnectionPublicId) params.set("legacy_connection_public_id", options.legacyConnectionPublicId)
  if (options.selectAccount) params.set("select_account", "1")
  if (options.reconsent) params.set("reconsent", "1")
  if (options.loginHintConnectionPublicId) {
    params.set("login_hint_connection_public_id", options.loginHintConnectionPublicId)
  }

  const query = params.toString()
  const base = `/portal/settings/calendar/${provider}/authorize`
  return query ? `${base}?${query}` : base
}

// True when the user already has a (visible) connection for this provider —
// the next connect should force the provider's account chooser.
export const hasConnectionForProvider = (
  connections: CalendarConnectionSummary[],
  provider: CalendarProvider
): boolean => visibleConnections(connections).some((connection) => connection.provider === provider)

// Practitioner-facing help for the two selection rows on each account card.
export const CONFLICT_SELECTION_TOOLTIP =
  "Pick calendars whose busy times should block new Heal.me bookings. This prevents double-booking against personal or other events."

export const WRITE_SELECTION_TOOLTIP =
  "Pick which calendar should receive new Heal.me appointments. If none is selected, bookings stay in Heal.me only and will not appear on this account."

export const WRITE_SELECTION_EMPTY_LABEL = "Not adding bookings — choose a calendar to sync"

export const CONFLICT_SELECTION_EMPTY_WARNING =
  "Busy times from this account are not blocking Heal.me bookings. Choose at least one calendar under Check for conflicts."

export const emptyConflictWarning = (connection: CalendarConnectionSummary): string | null => {
  if (connection.status !== "active") return null
  if (connection.calendars.length === 0) return null
  if (conflictSelectableCalendars(connection).some((calendar) => calendar.selectedForConflicts)) return null
  return CONFLICT_SELECTION_EMPTY_WARNING
}

// Conflict selection: any readable calendar can be checked for conflicts,
// including read-only feed calendars (conflict-only is a supported mode).
export const conflictSelectableCalendars = (connection: CalendarConnectionSummary): ExternalCalendarSummary[] =>
  connection.calendars.filter((calendar) => calendar.readable)

// Write selection: feeds never receive bookings, and only writable calendars
// qualify. Mirrors the backend guards in updateCalendarWriteSelections.
export const writeSelectableCalendars = (connection: CalendarConnectionSummary): ExternalCalendarSummary[] => {
  if (connection.provider === "ical_feed") return []

  return connection.calendars.filter((calendar) => calendar.writable)
}

// Legacy connections are editable too: the backend mirrors selections onto
// the Cronofy rows (`cronofy_calendars.active` / `write_calendar_id`).
export const supportsWriteSelection = (connection: CalendarConnectionSummary): boolean =>
  writeSelectableCalendars(connection).length > 0

// Legacy Cronofy writes bookings to a single calendar per account, so the
// write flyout behaves as a single-select for legacy connections.
export const singleWriteSelection = (connection: CalendarConnectionSummary): boolean =>
  connection.implementation === "cronofy"

// A legacy Cronofy connection is migratable in the UI when its provider has a
// native OAuth flow available in this environment and no native connection is
// linked yet. Linkage must come from `linkedConnectionPublicId` — never
// inferred from email/provider equality.
export const migratableLegacyConnection = (
  connection: CalendarConnectionSummary,
  connectableProviders: CalendarProvider[]
): boolean =>
  connection.implementation === "cronofy" &&
  !connection.linkedConnectionPublicId &&
  OAUTH_PROVIDERS.includes(connection.provider) &&
  isProviderConnectable(connection.provider, connectableProviders)

export const MIGRATION_COUNT_KEYS = ["pending", "matched", "recreated", "verified", "ambiguous", "failed"] as const

export type MigrationCounts = Record<(typeof MIGRATION_COUNT_KEYS)[number], number>

export const migrationCounts = (stats: Record<string, number> | null | undefined): MigrationCounts =>
  MIGRATION_COUNT_KEYS.reduce((counts, key) => {
    counts[key] = stats?.[key] || 0
    return counts
  }, {} as MigrationCounts)

// User-safe blocker copy. Codes come from CutoverGate/migration items; the
// fallback keeps unknown codes safe (no event titles/UIDs ever reach the UI).
export const BLOCKER_COPY: Record<string, string> = {
  native_sync_stale: "Your new calendar connection hasn't finished syncing recently.",
  ambiguous_items: "Some calendar events need review before switching over.",
  failed_items: "Some calendar events could not be transferred yet.",
  unverified_items: "Some calendar events are still being verified.",
  cronofy_operation_in_flight: "A previous calendar update is still in progress.",
  inconsistent_outbox: "Pending calendar updates need to finish before switching over.",
  shadow_false_free: "We found an availability difference that needs review.",
  missing_writable_native_calendar: "Choose a calendar for new bookings before switching over."
}

export const blockerCopy = (code: string): string =>
  BLOCKER_COPY[code] || "Something needs attention before switching over. Contact support with your reference code."

export const MIGRATION_STATE_LABELS: Record<string, string> = {
  pending: "Waiting to start",
  snapshotting: "Copying your events",
  matching: "Matching your events",
  verifying: "Verifying your events",
  cutover_ready: "Ready to switch",
  cutting_over: "Switching now",
  completed: "Switch complete",
  rollback: "Rolled back",
  blocked: "Needs attention"
}

export const migrationStateLabel = (state: string): string => MIGRATION_STATE_LABELS[state] || "In progress"

export const canStartCutover = (migration: CalendarMigrationStatus): boolean => migration.state === "cutover_ready"

export const SYNC_ACTIVITY_COPY = {
  syncing_calendars: "Syncing calendars…",
  updating_bookings: "Updating bookings on Google…",
  retrying: "Retrying sync…",
  resync_queued: "Resync queued…"
} as const

export const syncActivityMessage = (
  activity: CalendarConnectionSummary["syncActivity"],
  options: { resyncQueued?: boolean } = {}
): string | null => {
  if (options.resyncQueued && !activity) return SYNC_ACTIVITY_COPY.resync_queued
  if (!activity) return null

  if (activity.messageKey === "updating_bookings" && activity.pendingOperationsCount > 0) {
    const n = activity.pendingOperationsCount
    return n === 1 ? "Updating 1 booking on Google…" : `Updating ${n} bookings on Google…`
  }

  if (activity.messageKey in SYNC_ACTIVITY_COPY) {
    return SYNC_ACTIVITY_COPY[activity.messageKey as keyof typeof SYNC_ACTIVITY_COPY]
  }

  return SYNC_ACTIVITY_COPY.updating_bookings
}

// Migrations shown on a card are matched only through explicit public IDs.
export const migrationsForConnection = (
  migrations: CalendarMigrationStatus[],
  connectionPublicId: string
): CalendarMigrationStatus[] =>
  migrations.filter(
    (migration) =>
      migration.sourceConnectionPublicId === connectionPublicId ||
      migration.destinationConnectionPublicId === connectionPublicId
  )

export const activeMigrations = (status: Pick<CalendarIntegrationStatus, "migrations">): CalendarMigrationStatus[] =>
  status.migrations.filter((migration) => !["completed", "rollback"].includes(migration.state))

// Disconnect safety: warn when a migration is running for the connection or
// its migration state is mid-pipeline. (The status payload does not expose
// pending outbox writes yet — documented gap; do not fake that signal.)
export const disconnectWarning = (
  connection: CalendarConnectionSummary,
  migrations: CalendarMigrationStatus[]
): string | null => {
  const inPipeline = !["not_started", "completed"].includes(connection.migrationState)
  const hasActiveMigration = migrationsForConnection(migrations, connection.publicId).some(
    (migration) => !["completed", "rollback"].includes(migration.state)
  )

  if (hasActiveMigration || inPipeline) {
    return "A calendar migration is in progress for this account. Disconnecting now may interrupt it."
  }

  return null
}

export const lastSyncLabel = (lastSyncedAt: string | null, now: Date = new Date()): string => {
  if (!lastSyncedAt) return "Not synced yet"

  const synced = new Date(lastSyncedAt)
  if (Number.isNaN(synced.getTime())) return "Not synced yet"

  const diffMinutes = Math.max(0, Math.floor((now.getTime() - synced.getTime()) / 60000))
  if (diffMinutes < 1) return "Synced just now"
  if (diffMinutes < 60) return `Synced ${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`

  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `Synced ${diffHours} hour${diffHours === 1 ? "" : "s"} ago`

  const diffDays = Math.floor(diffHours / 24)
  return `Synced ${diffDays} day${diffDays === 1 ? "" : "s"} ago`
}
